import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../../contexts/AuthContext';
import ModalBase from '../ModalBase';
import ExtratoMovimentacoes from './ExtratoMovimentacoes';
import { ModalMovimentar, ModalProduto, ModalRetorno } from './EstoqueModais';
import { successAlert } from '../../helpers/alerts';
import {
  PERIODOS, STATUS_PRODUTO, formatarCodigo, fmtNum, nomeProduto, periodo,
} from '../../helpers/estoque';

// Aba Estoque: painel do periodo e a area Produtos — lista unica filtrada
// enquanto digita, com Adicionar quantidade / Movimentar / Extrato / Editar /
// Excluir em cada linha.
//
// Produto = identidade (codigo fixo). O saldo so muda por movimentacao.

function normalizar(t) {
  return String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function Painel({ versao }) {
  const [chave, setChave] = useState('hoje');
  const [datas, setDatas] = useState(periodo('hoje'));
  const [resumo, setResumo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/insumos/resumo`, { params: datas })
      .then((res) => setResumo(res.data.data))
      .catch(() => setResumo(null));
  }, [datas, versao]);

  const cards = resumo ? [
    ['Produtos cadastrados', resumo.produtos_ativos, 'bi-box-seam', `${resumo.produtos_total} no total`],
    ['Entradas', fmtNum(resumo.entradas), 'bi-box-arrow-in-down', 'unidades'],
    ['Saídas', fmtNum(resumo.saidas), 'bi-box-arrow-up', 'unidades'],
    ['Devoluções', fmtNum(resumo.devolucoes), 'bi-arrow-return-left', 'unidades'],
    ['Gasto', fmtNum(resumo.gasto), 'bi-tools', 'consumido de fato'],
    ['Ajustes', resumo.ajustes, 'bi-sliders', 'lançamentos'],
    ['Abaixo do mínimo', resumo.abaixo_minimo, 'bi-exclamation-triangle', 'produtos ativos'],
  ] : [];

  return (
    <section className="content-card">
      <div className="insumo-ck__secao-topo">
        <h3 className="insumo-ck__secao">Resumo do estoque</h3>
        <div className="vw-chips">
          {PERIODOS.map(([k, label]) => (
            <button
              key={k} type="button" className={`vw-chip ${chave === k ? 'vw-chip--on' : 'vw-chip--off'}`}
              onClick={() => { setChave(k); if (k !== 'personalizado') setDatas(periodo(k)); }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {chave === 'personalizado' ? (
        <div className="portal-form-actions" style={{ marginBottom: 12 }}>
          <label className="portal-field"><span>De</span>
            <input type="date" value={datas.de} onChange={(e) => setDatas((d) => ({ ...d, de: e.target.value }))} />
          </label>
          <label className="portal-field"><span>Até</span>
            <input type="date" value={datas.ate} onChange={(e) => setDatas((d) => ({ ...d, ate: e.target.value }))} />
          </label>
        </div>
      ) : null}
      <div className="estoque-painel">
        {cards.map(([label, valor, icon, sub]) => (
          <div key={label} className="estoque-painel__card">
            <i className={`bi ${icon}`} />
            <span>{label}</span>
            <strong>{valor}</strong>
            <small>{sub}</small>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function EstoqueProdutos({ onAbrirExtrato, onMudou }) {
  const [produtos, setProdutos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [pendentes, setPendentes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('ativo');
  const [catId, setCatId] = useState('');

  const [modal, setModal] = useState(null); // { tipo, ... }
  const [versao, setVersao] = useState(0); // recarrega o painel apos movimentar

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [p, c, m, t, pend] = await Promise.all([
        axios.get(`${API}/insumos`, { params: { status } }),
        axios.get(`${API}/insumos/categorias`),
        axios.get(`${API}/insumos/marcas`),
        axios.get(`${API}/insumos/tipos-movimentacao`),
        axios.get(`${API}/insumos/movimentacoes/pendentes-retorno`),
      ]);
      setProdutos(p.data.data || []);
      setCategorias(c.data.data || []);
      setMarcas(m.data.data || []);
      setTipos(t.data.data || []);
      setPendentes(pend.data.data || []);
      setErro('');
    } catch (err) {
      setErro(err.response?.data?.message || 'Não foi possível carregar o estoque.');
    } finally {
      setCarregando(false);
    }
  }, [status]);

  useEffect(() => { carregar(); }, [carregar]);

  const depoisDeSalvar = () => {
    setModal(null);
    carregar();
    setVersao((v) => v + 1);
    onMudou?.();
  };

  const principais = categorias.filter((c) => !c.parent_id && c.ativo);

  // Filtra enquanto digita: codigo (003 / 3), nome, categoria, marca, modelo.
  const termo = normalizar(busca.trim());
  const filtrados = useMemo(() => produtos.filter((p) => {
    if (catId && p.categoria_id !== Number(catId)) return false;
    if (!termo) return true;
    const codigo = /^\d+$/.test(termo) && Number(termo) === Number(p.numero);
    const texto = normalizar([p.descricao, p.detalhe, p.categoria_nome, p.subcategoria_nome, p.marca_nome, p.modelo].join(' '));
    return codigo || texto.includes(termo);
  }), [produtos, termo, catId]);

  const excluir = async (p) => {
    const ok = await Swal.fire({
      title: 'Excluir produto?',
      html: `<strong>${formatarCodigo(p.numero)} — ${nomeProduto(p)}</strong><br><br>`
        + 'Se ele já teve movimentações, sai da lista e o histórico fica guardado (filtro "Inativos"). '
        + 'O código nunca é reaproveitado.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
    });
    if (!ok.isConfirmed) return;
    try {
      const res = await axios.delete(`${API}/insumos/${p.id}`);
      depoisDeSalvar();
      successAlert({ title: 'Produto excluído', text: res.data?.message || '' });
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.message || 'Falha ao excluir.', 'error');
    }
  };

  return (
    <div className="insumo-ck">
      <Painel versao={versao} />

      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      <section className="content-card">
        <div className="insumo-ck__secao-topo">
          <h3 className="insumo-ck__secao">
            Produtos <span className="cell-muted">({filtrados.length})</span>
          </h3>
          <div className="portal-form-actions">
            <button type="button" className="btn-portal btn-portal--primary" onClick={() => setModal({ tipo: 'produto' })}>
              <i className="bi bi-plus-circle" /> Cadastrar Novo Produto
            </button>
            <button type="button" className="btn-portal btn-portal--outline" onClick={onAbrirExtrato}>
              <i className="bi bi-journal-text" /> Extrato de Movimentações
            </button>
            <button type="button" className="btn-portal btn-portal--outline" onClick={() => setModal({ tipo: 'pendentes' })}>
              <i className="bi bi-arrow-return-left" /> Retornos pendentes{pendentes.length ? ` (${pendentes.length})` : ''}
            </button>
          </div>
        </div>

        <div className="estoque-filtros">
          <div className="estoque-filtros__busca">
            <i className="bi bi-search" />
            <input
              type="text" value={busca} onChange={(e) => setBusca(e.target.value)}
              placeholder="Pesquisar por código, nome, marca ou modelo..."
            />
          </div>
          <select value={catId} onChange={(e) => setCatId(e.target.value)}>
            <option value="">Todas as categorias</option>
            {principais.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ativo">Ativos</option>
            <option value="inativo">Inativos</option>
            <option value="descontinuado">Descontinuados</option>
            <option value="todos">Todos os status</option>
          </select>
        </div>

        {carregando ? (
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando produtos...</p>
          </div>
        ) : filtrados.length ? (
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Código</th><th>Produto</th><th>Marca / modelo</th><th>Estoque</th><th>Status</th><th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((p) => {
                  const baixo = Number(p.estoque_minimo) > 0 && Number(p.estoque_atual) <= Number(p.estoque_minimo);
                  const st = STATUS_PRODUTO[p.status] || { label: p.status, pill: '' };
                  return (
                    <tr key={p.id}>
                      <td><span className="cell-strong">{formatarCodigo(p.numero)}</span></td>
                      <td>
                        <span className="cell-strong">{p.descricao}</span>
                        <div className="insumo-ck__detalhe">
                          {p.categoria_nome || 'Sem categoria'}{p.subcategoria_nome ? ` › ${p.subcategoria_nome}` : ''}
                          {p.detalhe ? ` · ${p.detalhe}` : ''}
                        </div>
                      </td>
                      <td className="cell-muted">{[p.marca_nome, p.modelo].filter(Boolean).join(' ') || '—'}</td>
                      <td>
                        <span className={`permissao-chip ${baixo ? '' : 'permissao-chip--on'}`}>
                          {fmtNum(p.estoque_atual)} {p.unidade}
                        </span>
                        {baixo ? <div className="insumo-ck__detalhe">mínimo {fmtNum(p.estoque_minimo)}</div> : null}
                      </td>
                      <td><span className={`status-pill ${st.pill}`}>{st.label}</span></td>
                      <td>
                        <div className="table-actions estoque-acoes">
                          {p.status === 'ativo' ? (
                            <button
                              type="button" className="btn-portal btn-portal--primary" title="Adicionar quantidade"
                              onClick={() => setModal({ tipo: 'movimentar', produto: p, tipoInicial: 'reposicao', simples: true })}
                            >
                              <i className="bi bi-plus-lg" /> Adicionar
                            </button>
                          ) : null}
                          <button type="button" className="btn-portal btn-portal--outline" title="Saída, devolução, ajuste..." onClick={() => setModal({ tipo: 'movimentar', produto: p })}>
                            <i className="bi bi-arrow-down-up" /> Movimentar
                          </button>
                          <button type="button" className="btn-portal btn-portal--outline" title="Ver extrato" onClick={() => setModal({ tipo: 'extrato', produto: p })}>
                            <i className="bi bi-journal-text" />
                          </button>
                          <button type="button" className="btn-portal btn-portal--outline" title="Editar cadastro" onClick={() => setModal({ tipo: 'produto', produto: p })}>
                            <i className="bi bi-pencil" />
                          </button>
                          <button type="button" className="btn-portal btn-portal--outline estoque-acoes__excluir" title="Excluir produto" onClick={() => excluir(p)}>
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <strong>Nenhum produto {termo || catId ? 'encontrado' : 'cadastrado'}</strong>
            <span>{termo || catId ? 'Tente outro termo, categoria ou status.' : 'Use "Cadastrar Novo Produto" para incluir.'}</span>
          </div>
        )}
      </section>

      {modal?.tipo === 'produto' ? (
        <ModalProduto
          produto={modal.produto}
          produtos={produtos}
          categorias={categorias}
          marcas={marcas}
          onClose={() => setModal(null)}
          onCategoriaCriada={(c) => setCategorias((cs) => [...cs, { ...c, ativo: true }])}
          onMarcaCriada={(m) => setMarcas((ms) => (ms.some((x) => x.id === m.id) ? ms : [...ms, m].sort((a, b) => a.nome.localeCompare(b.nome))))}
          onAdicionarEstoque={(p) => setModal({ tipo: 'movimentar', produto: p, tipoInicial: 'reposicao', simples: true })}
          onSalvo={(p, novo) => {
            depoisDeSalvar();
            if (novo) {
              successAlert({ title: 'Produto cadastrado', text: `${nomeProduto(p)} — código ${formatarCodigo(p.numero)}.` });
            }
          }}
        />
      ) : null}

      {modal?.tipo === 'movimentar' ? (
        <ModalMovimentar
          produto={modal.produto}
          tipos={tipos}
          tipoInicial={modal.tipoInicial}
          simples={!!modal.simples}
          onClose={() => setModal(null)}
          onSalvo={depoisDeSalvar}
        />
      ) : null}

      {modal?.tipo === 'extrato' ? (
        <ModalBase titulo={`${nomeProduto(modal.produto)} — código ${formatarCodigo(modal.produto.numero)}`} onClose={() => setModal(null)} largura={1100}>
          <p style={{ marginTop: 0 }}>
            Estoque atual: <strong>{fmtNum(modal.produto.estoque_atual)} {modal.produto.unidade}</strong>
            <span className="cell-muted"> · {modal.produto.categoria_nome}{modal.produto.subcategoria_nome ? ` › ${modal.produto.subcategoria_nome}` : ''}</span>
          </p>
          <ExtratoMovimentacoes produto={modal.produto} tipos={tipos} />
        </ModalBase>
      ) : null}

      {modal?.tipo === 'pendentes' ? (
        <ModalBase titulo="Saídas para manutenção com retorno pendente" onClose={() => setModal(null)} largura={820}>
          {pendentes.length ? (
            <div className="table-shell">
              <table className="portal-table">
                <thead><tr><th>Saída</th><th>Produto</th><th>Retirado</th><th>Quem / onde</th><th /></tr></thead>
                <tbody>
                  {pendentes.map((s) => (
                    <tr key={s.id}>
                      <td className="cell-muted">{new Date(s.created_at).toLocaleString('pt-BR')}</td>
                      <td><span className="cell-strong">{s.produto}</span><div className="insumo-ck__detalhe">código {formatarCodigo(s.numero)}</div></td>
                      <td>{fmtNum(s.quantidade)}</td>
                      <td className="cell-muted">{[s.autor, s.filial, s.destino].filter(Boolean).join(' · ') || '—'}</td>
                      <td>
                        <button type="button" className="btn-portal btn-portal--primary" onClick={() => setModal({ tipo: 'retorno', saida: s })}>
                          Registrar retorno
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="cell-muted">Nenhuma saída para manutenção aguardando retorno.</p>}
        </ModalBase>
      ) : null}

      {modal?.tipo === 'retorno' ? (
        <ModalRetorno saida={modal.saida} onClose={() => setModal({ tipo: 'pendentes' })} onSalvo={depoisDeSalvar} />
      ) : null}
    </div>
  );
}

